import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import SortIcon from "@mui/icons-material/Sort";
import GridViewIcon from "@mui/icons-material/GridView";
import {Add, FileDownloadOutlined} from "@mui/icons-material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Button from "../../../../components/base/button/button";
import SearchInput from "../../../../components/base/input/search_input";
import Api from "../../../../../core/services/api/api";
import notebookTags, {getCustomerAggregateNoteTagGroups} from "../../../../../core/models/static/routes/notebook-tags";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";
import useCustomerNoteSectionVisibility from "../../../../hooks/use-customer-note-section-visibility";
import {useAppSelector} from "../../../../redux";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import {dateComparator, exportCSVFile} from "../../../../../core/services/utils/utils";
import {buildNotesCsvExportInfo} from "../../../../../core/services/notebook/export-notes";
import DateUtils from "../../../../../core/services/date-utils";
import useAccessLevels from "../../../../hooks/use-access-levels";
import CustomerNotesGridView from "./views/grid";
import CustomerNotesListView from "./views/list";
import {Typography} from "@mui/material";


/**
 * Strips HTML tags for keyword matching on note body text.
 * @param {string | undefined} html
 * @return {string}
 */
const stripHtml = (html) => (html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const CustomerNotesView = () => {
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [gridView, setGridView] = useState(true);
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const sectionVisibility = useCustomerNoteSectionVisibility();
    const {configureNotebookTags, toggleFabNotes} = useConfigureNotebook('customer-notes-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {packages} = useAccessLevels();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customer-notes'});

    const canExportNotes = packages.base.exportCustomerNotes;

    const tagGroups = useMemo(() => {
        if (!defaultCustomer?.code || !defaultCustomer?.id) {
            return [];
        }
        return getCustomerAggregateNoteTagGroups(defaultCustomer.code, defaultCustomer.id, sectionVisibility);
    }, [defaultCustomer?.code, defaultCustomer?.id, sectionVisibility]);

    /**
     * Hides FAB notes on this page and configures page-scoped notebook tags.
     */
    useLayoutEffect(() => {
        toggleFabNotes(false);
    }, [toggleFabNotes]);

    /**
     * Configures notebook tags and behavioral analytics for this view.
     */
    useEffect(() => {
        if (!defaultCustomer?.code) {
            return;
        }
        configureNotebookTags((tags) => tags.customerNotes(defaultCustomer.code));
        configureBehavioralTool((tags) => tags.customerNotes(defaultCustomer.code));
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer?.code]);

    /**
     * Fetches notes for a single tag group.
     */
    const getNotesForTagGroup = useCallback(async (tagGroup) => {
        const size = 20;
        const initialResponse = await Api.getPageNotes({
            tags: tagGroup,
            pagination: {currentPage: 1, pageSize: size},
        });
        if (!isMounted()) {
            return [];
        }
        if (!initialResponse?.resultFlag) {
            return [];
        }
        if (initialResponse?.data?.totalCount > size) {
            const fullResponse = await Api.getPageNotes({
                tags: tagGroup,
                pagination: {
                    currentPage: 1,
                    pageSize: Math.max(initialResponse?.data?.totalCount ?? 0, 1),
                },
            });
            if (!isMounted()) {
                return [];
            }
            return fullResponse?.data?.items ?? [];
        }
        return initialResponse?.data?.items ?? [];
    }, [isMounted]);

    /**
     * Loads and deduplicates notes across all customer tag groups.
     */
    const loadNotes = useCallback(async () => {
        if (!tagGroups.length) {
            setNotes([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const groupResults = await Promise.all(tagGroups.map((tagGroup) => getNotesForTagGroup(tagGroup)));
        if (!isMounted()) {
            return;
        }
        const uniqueNotes = Array.from(
            new Map(groupResults.flat().map((note) => [note.id, note])).values(),
        ).sort((a, b) => dateComparator(b.modifiedDateTime ?? b.createdDateTime, a.modifiedDateTime ?? a.createdDateTime));
        setNotes(uniqueNotes);
        setLoading(false);
    }, [getNotesForTagGroup, isMounted, tagGroups]);

    /**
     * Loads notes when customer context or tag groups change.
     */
    useEffect(() => {
        void loadNotes();
    }, [loadNotes]);

    /**
     * Filters notes by keyword against title and body text.
     */
    const filteredNotes = useMemo(() => {
        const normalizedKeyword = keyword.trim().toLowerCase();
        if (!normalizedKeyword) {
            return notes;
        }
        return notes.filter((note) => {
            const title = note?.title?.toLowerCase() ?? '';
            const body = stripHtml(note?.body).toLowerCase();
            return title.includes(normalizedKeyword) || body.includes(normalizedKeyword);
        });
    }, [keyword, notes]);

    /**
     * Opens the dialog to create a note tagged for the customer Notes page.
     */
    const addNewNote = useCallback(() => {
        if (!defaultCustomer?.code) {
            return;
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {
            open: true,
            note: null,
            tagList: notebookTags.customerNotes(defaultCustomer.code),
            onSuccess: (note) => setNotes((prevState) => [note, ...prevState]),
        }));
    }, [defaultCustomer?.code, dispatch]);

    /**
     * Opens the dialog to edit an existing note.
     */
    const openUpsertDialog = useCallback((note) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {
            open: true,
            note: note,
            onSuccess: (updatedNote) => setNotes((prevState) =>
                prevState.map((entry) => entry.id === updatedNote.id ? updatedNote : entry),
            ),
        }));
    }, [dispatch]);

    /**
     * Toggles a note's public/private visibility.
     */
    const toggleNoteIsPrivate = useCallback(async (note, isPrivate) => {
        const forApi = {
            noteId: note.id,
            title: note.title,
            body: note.body,
            isPrivate: isPrivate,
            tagList: note.tagList,
            updatedProperties: ['IsPrivate'],
        };
        const response = await Api.updateNote(forApi);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setNotes((prevState) => prevState.map((entry) =>
                entry.id === note.id ? {...entry, isPrivate: isPrivate} : entry,
            ));
        }
        return response;
    }, [isMounted]);

    /**
     * Removes a note by id.
     */
    const removeNote = useCallback(async (noteId) => {
        const response = await Api.removeNote(noteId);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setNotes((prevState) => prevState.filter((entry) => entry.id !== noteId));
        }
    }, [isMounted]);

    /**
     * Updates a note in the local list after edit.
     */
    const setNote = useCallback((noteId, updatedNote) => {
        setNotes((prevState) => prevState.map((entry) => entry.id === noteId ? updatedNote : entry));
    }, []);

    /**
     * Updates keyword filter from search input.
     */
    const searchOverRecords = useCallback((value) => {
        setKeyword(value ?? '');
    }, []);

    /**
     * Switches between grid and list layouts.
     */
    const changeView = useCallback((view) => {
        setGridView(view !== 'list');
    }, []);

    const notesExportColumns = useMemo(() => ({
        title: t('list.columns.title'),
        operator: t('list.columns.operator'),
        modifiedDate: t('list.columns.modifiedDate'),
        source: t('list.columns.source'),
        visibility: t('list.columns.visibility'),
        body: t('list.columns.note'),
        publicVisibility: t('list.visibility.public'),
        privateVisibility: t('list.visibility.private'),
    }), [t]);

    /**
     * Exports filtered customer notes to CSV (includes HTML body).
     */
    const exportNotesToCsv = useCallback(async () => ({
        ...buildNotesCsvExportInfo(filteredNotes, {
            title: `${t('exportTitle')} - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`,
            columns: notesExportColumns,
        }),
    }), [filteredNotes, notesExportColumns, t]);

    /**
     * Triggers CSV download (grid view header button).
     */
    const downloadNotesCsv = useCallback(async () => {
        const exportInfo = await exportNotesToCsv();
        if (!exportInfo?.body?.length) {
            return;
        }
        exportCSVFile(exportInfo.body, exportInfo.title);
    }, [exportNotesToCsv]);

    /**
     * Toggle button styling aligned with NCR notes list.
     */
    const toggleButtonGroupMixin = useCallback((theme) => ({
        border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
        '& .MuiToggleButton-root': {
            backgroundColor: 'transparent',
            borderColor: 'transparent !important',
            color: theme.palette.text.primary,
            height: 30,
            marginBlock: 0.5,
            marginInline: 0.5,
            borderRadius: 1,
            paddingBlock: 0.5,
            paddingInline: 0.5,
        },
        '& .MuiToggleButton-root:hover': {
            backgroundColor: theme.palette.background.tertiary,
            borderColor: `${theme.palette.background.tertiary} !important`,
            color: theme.palette.text.primary,
        },
        '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: theme.palette.background.tertiary,
            borderColor: `${theme.palette.background.tertiary} !important`,
            color: theme.palette.primary.main,
        },
        '& .MuiToggleButton-root.Mui-selected:hover': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.background.tertiary,
            borderColor: `${theme.palette.background.tertiary} !important`,
        },
    }), []);

    return (
        <Grid container spacing={2} className={'customer-notes-list'} sx={{padding: 2}}>
            <Grid container columns={12} xs={12} className={'header'} alignItems={'center'}>
                <Grid xs>
                    <Typography variant={'h6'} fontWeight={600}>
                        {t('title')}
                    </Typography>
                </Grid>
                <Grid xs={'auto'} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                    {
                        canExportNotes &&
                        <Button
                            className={'d-flex align-items-center justify-content-center'}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={downloadNotesCsv}
                            disabled={loading || filteredNotes.length < 1}
                        >
                            <FileDownloadOutlined className={'icon small'} style={{margin: '2px 0'}}/>
                            {t('exportButton')}
                        </Button>
                    }
                    <Button
                        className={'d-flex align-items-center justify-content-center'}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={addNewNote}
                        disabled={loading || !defaultCustomer?.code}
                    >
                        <Add className={'icon small'} style={{margin: '2px 0'}}/>
                        {t('addNote')}
                    </Button>
                    <ToggleButtonGroup
                        spacing={2}
                        className={'button-group'}
                        orientation="horizontal"
                        exclusive
                        onChange={(_event, value) => value && changeView(value)}
                        sx={toggleButtonGroupMixin}
                    >
                        <ToggleButton
                            selected={!gridView}
                            value={'list'}
                            className={'mr-1'}
                            style={{borderRadius: 5}}
                            aria-label={t('listView')}
                        >
                            <SortIcon/>
                        </ToggleButton>
                        <ToggleButton
                            selected={gridView}
                            value={'grid'}
                            style={{borderRadius: 5}}
                            aria-label={t('gridView')}
                        >
                            <GridViewIcon/>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>
            <Grid xs={12}>
                <SearchInput
                    initialValue={keyword}
                    loading={loading}
                    autoSearch
                    immediateSearch
                    search={searchOverRecords}
                    label={t('searchLabel')}
                />
            </Grid>
            <Grid xs={12}>
                {
                    gridView &&
                    <CustomerNotesGridView
                        notes={filteredNotes}
                        loading={loading}
                        toggleNoteIsPrivate={toggleNoteIsPrivate}
                        setNote={setNote}
                        removeNote={removeNote}
                    />
                }
                {
                    !gridView &&
                    <CustomerNotesListView
                        notes={filteredNotes}
                        loading={loading}
                        updateNote={openUpsertDialog}
                        removeNote={removeNote}
                        getExportInfo={canExportNotes ? exportNotesToCsv : undefined}
                    />
                }
            </Grid>
        </Grid>
    );
};

export default CustomerNotesView;
